import type {
  EquipmentCyclePolicy,
  EquipmentCycleEventReference,
  EquipmentCycleExpiryRule,
  EquipmentCycleTimeExpression,
  EquipmentStateAction,
  EquipmentStateAttribute,
  EquipmentStateCatalogVersion,
  EquipmentStateClassModel,
  EquipmentStateCondition,
  EquipmentStateEffect,
  EquipmentStateGuardOperator,
  EquipmentStateTemporalTransition,
  EquipmentStateTimeDrivenTransition,
  EquipmentTemporalExpression,
} from '../../types/prodEquipmentStateCatalog';
import { displayAttributeCode, isAutoAttributeCode } from './attributeCodeGeneration';

export const VERSION_STATUS_LABELS: Record<EquipmentStateCatalogVersion['status'], string> = {
  DRAFT: '草稿',
  VALIDATED: '已校验',
  PUBLISHED: '已发布',
  SUPERSEDED: '已取代',
  ABANDONED: '已放弃',
};

export const RESOURCE_KIND_LABELS = {
  ROOM: '房间',
  EQUIPMENT: '设备',
  COMPONENT: '组件',
  CIP_STATION: 'CIP 站',
} as const;

export const ATTRIBUTE_KIND_LABELS = {
  DISCRETE: '离散状态',
  COUNTER: '计数消耗',
  CLOCK: '期限时钟',
} as const;

export const FABRICATION_TYPE_LABELS = {
  SS: '不锈钢 SS',
  SUS: '一次性 SUS',
  FIXED: '固定设施',
  CONSUMABLE: '耗材',
  OTHER: '其他',
} as const;

export const ACTION_ORIGIN_LABELS = {
  PRIMARY: '主链动作',
  DERIVABLE: '可派生动作',
} as const;

export const EFFECT_TIMING_LABELS = {
  START: '操作开始时生效',
  END: '操作结束时生效',
} as const;

export const GUARD_OPERATOR_LABELS: Record<EquipmentStateGuardOperator, string> = {
  EQ: '等于',
  NE: '不等于',
  LT: '小于',
  LTE: '小于等于',
  GT: '大于',
  GTE: '大于等于',
  IN: '属于集合',
  NOT_IN: '不属于集合',
  IS_SET: '已设置',
  IS_NOT_SET: '未设置',
  NOT_EXPIRED: '未过期',
  EXPIRED: '已过期',
};

export const EFFECT_TYPE_LABELS = {
  SET_STATE: '设置状态',
  ACTIVATE_TEMPORAL_STATE: '激活周期状态',
  INCREMENT: '增加计数',
  RESET_COUNTER: '计数复位',
  START_TIMER: '启动时钟',
  CLEAR_TIMER: '清除时钟',
} as const;

export const isWritableCatalogVersion = (version: EquipmentStateCatalogVersion | null | undefined) => (
  version?.status === 'DRAFT' || version?.status === 'VALIDATED'
);

export const conditionNeedsValue = (operator: EquipmentStateGuardOperator): boolean => (
  !['IS_SET', 'IS_NOT_SET', 'NOT_EXPIRED', 'EXPIRED'].includes(operator)
);

const displayValue = (value: EquipmentStateCondition['value']): string => {
  if (Array.isArray(value)) return value.join('、');
  return value == null || value === '' ? '—' : String(value);
};

export const formatCondition = (condition: EquipmentStateCondition): string => {
  const operator = GUARD_OPERATOR_LABELS[condition.operator];
  return conditionNeedsValue(condition.operator)
    ? `${displayAttributeCode(condition.attributeCode)} ${operator} ${displayValue(condition.value)}`
    : `${displayAttributeCode(condition.attributeCode)} ${operator}`;
};

export const formatEffect = (effect: EquipmentStateEffect): string => {
  const label = EFFECT_TYPE_LABELS[effect.effectType];
  return effect.effectType === 'SET_STATE' || effect.effectType === 'INCREMENT'
    ? `${displayAttributeCode(effect.attributeCode)} · ${label} ${displayValue(effect.value)}`
    : `${displayAttributeCode(effect.attributeCode)} · ${label}`;
};

const TEMPORAL_UNIT_LABELS: Record<EquipmentTemporalExpression['unit'], string> = {
  SECOND: '秒',
  MINUTE: '分钟',
  HOUR: '小时',
  DAY: '天',
  WEEK: '周',
  MONTH: '个月',
  QUARTER: '个季度',
  YEAR: '年',
};

export const formatTemporalExpression = (
  expression: EquipmentTemporalExpression | EquipmentCycleTimeExpression | null,
): string => {
  if (!expression) return '未设置';
  const basis = expression.basis === 'CALENDAR_PERIOD' ? '日历' : '连续时长';
  return `${expression.value} ${TEMPORAL_UNIT_LABELS[expression.unit]} · ${basis}`;
};

export const CYCLE_POLICY_TYPE_LABELS: Record<EquipmentCycleExpiryRule['kind'], string> = {
  ELAPSED: '经过连续时长',
  CALENDAR: '经过自然日历周期',
  ABSOLUTE_DATE_TIME: '固定截止时刻',
  SCHEDULE: '按日历排期',
  ACCUMULATED: '仪表 / 信号累计增量',
  THRESHOLD: '测量值达到阈值',
  CONDITION: '业务条件成立',
  EVENT: '发生指定事件',
  COMPOSITE: '多个条件组合',
};

export const formatCycleRule = (rule: EquipmentCycleExpiryRule): string => {
  if (rule.kind === 'ELAPSED') return formatTemporalExpression(rule.duration);
  if (rule.kind === 'CALENDAR') return formatTemporalExpression(rule.period);
  if (rule.kind === 'ABSOLUTE_DATE_TIME') return `固定截止 · ${rule.fixedDateTime || '未设置'}`;
  if (rule.kind === 'ACCUMULATED') {
    return `${rule.source.code || '未选择来源'} 累计增加 ${rule.delta} ${rule.unit}`;
  }
  if (rule.kind === 'THRESHOLD') {
    return `${rule.source.code || '未选择来源'} ${rule.operator} ${rule.threshold} ${rule.unit}`;
  }
  if (rule.kind === 'CONDITION') {
    return `${rule.signalCode} ${rule.operator} ${String(rule.threshold)}${rule.unit ? ` ${rule.unit}` : ''}`;
  }
  if (rule.kind === 'EVENT') return `发生 ${rule.event.kind} 事件`;
  if (rule.kind === 'COMPOSITE') {
    return `${rule.operator === 'ALL_OF' ? '全部满足' : '任一满足'} · ${rule.rules.length} 条子规则`;
  }
  if (rule.schedule.kind === 'BUSINESS_INTERVAL') {
    return `${rule.schedule.value} ${rule.schedule.unit === 'BUSINESS_DAY' ? '工作日' : '工作周'} · ${rule.schedule.businessCalendarCode}`;
  }
  const pattern = rule.schedule.pattern;
  if (pattern.kind === 'WEEKLY') return `每 ${pattern.intervalWeeks} 周 · ${pattern.weekdays.join('、')} · ${rule.schedule.localTime}`;
  if (pattern.kind === 'MONTHLY_DAY') return `每 ${pattern.intervalMonths} 月 · 第 ${pattern.dayOfMonth} 日 · ${rule.schedule.localTime}`;
  if (pattern.kind === 'MONTHLY_NTH_WEEKDAY') {
    return `每 ${pattern.intervalMonths} 月 · ${pattern.ordinal} ${pattern.weekday} · ${rule.schedule.localTime}`;
  }
  return `每 ${pattern.intervalYears} 年 · ${pattern.month} 月 ${pattern.day} 日 · ${rule.schedule.localTime}`;
};

const formatCycleEventReference = (event: EquipmentCycleEventReference): string => {
  if (event.kind === 'CLASS_ACTIVATION') return '设备类别启用';
  if (event.kind === 'RESOURCE_COMMISSIONED_AT') return '设备投运';
  if (event.kind === 'CYCLE_SATISFIED') return '确认本周期已满足';
  if (event.kind === 'STATE_ENTRY') return `进入状态 ${displayAttributeCode(event.attributeCode)}/${event.valueCode}`;
  if (event.kind === 'ACTION_STATUS') {
    return `动作 ${event.actionCode} ${event.status === 'RELEASED' ? '已放行' : '已完成'}`;
  }
  if (event.kind === 'METER_RESET') return `仪表 ${event.meterCode} 复位`;
  if (event.kind === 'CUSTOM_EVENT') return `事件 ${event.eventCode}`;
  return event.kind;
};

export const formatCyclePolicySummary = (policy: EquipmentCyclePolicy): string => {
  const start = policy.startRule.kind === 'FIXED_DATE_TIME'
    ? `固定时刻 ${policy.startRule.fixedDateTime || '未设置'}`
    : formatCycleEventReference(policy.startRule.event);
  const refresh = policy.refreshRule.kind === 'ONE_SHOT'
    ? '不创建后继周期'
    : policy.refreshRule.kind === 'ROLL_FORWARD_FROM_DUE'
      ? '从上次到期点推进'
      : `${formatCycleEventReference(policy.refreshRule.event)}后重新创建`;
  return `开始：${start}；后继：${refresh}；到期：${formatCycleRule(policy.expiryRule)}`;
};

export const isPredictableCycleDueRule = (rule: EquipmentCycleExpiryRule): boolean => (
  ['ELAPSED', 'CALENDAR', 'SCHEDULE', 'ABSOLUTE_DATE_TIME'].includes(rule.kind)
  || (rule.kind === 'COMPOSITE' && rule.rules.every(isPredictableCycleDueRule))
);

export const cloneEquipmentStateModel = (model: EquipmentStateClassModel): EquipmentStateClassModel => (
  JSON.parse(JSON.stringify(model)) as EquipmentStateClassModel
);

export const createEmptyEquipmentStateModel = (): EquipmentStateClassModel => ({
  attributes: [],
  actions: [],
  readyConditions: [],
  cyclePolicies: [],
});

export interface LocalModelProblem {
  key: string;
  message: string;
}

export interface EquipmentStateTransitionPreview {
  key: string;
  attributeCode: string;
  from: string;
  to: string;
  actionCode: string;
  actionName: string;
  effectTiming: EquipmentStateAction['effectTiming'];
  origin: EquipmentStateAction['origin'];
  crossAttributeGuards: string[];
  otherEffects: string[];
}

export const deriveTransitionPreviews = (
  model: EquipmentStateClassModel,
): EquipmentStateTransitionPreview[] => model.actions.flatMap((action) => (
  action.effects
    .filter((effect) => effect.effectType === 'SET_STATE')
    .map((effect, index) => {
      const sameAttributeGuard = action.guards.find((guard) => (
        guard.attributeCode === effect.attributeCode && guard.operator === 'EQ'
      ));
      return {
        key: `${action.actionCode}-${effect.attributeCode}-${index}`,
        attributeCode: effect.attributeCode,
        from: sameAttributeGuard ? String(sameAttributeGuard.value ?? '任意') : '任意',
        to: String(effect.value ?? '—'),
        actionCode: action.actionCode,
        actionName: action.name,
        effectTiming: action.effectTiming,
        origin: action.origin,
        crossAttributeGuards: action.guards
          .filter((guard) => guard !== sameAttributeGuard)
          .map(formatCondition),
        otherEffects: action.effects
          .filter((item) => item !== effect)
          .map(formatEffect),
      };
    })
));

export interface EquipmentStateTimeDrivenTransitionPreview {
  key: string;
  attributeCode: string;
  sourceValueCode: string;
  targetValueCode: string;
  startLabel: string;
  refreshLabel: string;
  expiryLabel: string;
}

export const formatTimeDrivenTransitionStart = (
  transition: EquipmentStateTimeDrivenTransition,
): string => transition.startRule.kind === 'STATE_ENTRY'
  ? '进入来源状态时'
  : `动作 ${transition.startRule.actionCode} 投影时`;

export const formatTimeDrivenTransitionRefresh = (
  transition: EquipmentStateTimeDrivenTransition,
): string => transition.refreshRule.kind === 'RESET_ON_START_EVENT'
  ? '再次进入来源状态时重置'
  : transition.refreshRule.kind === 'ONE_SHOT'
    ? '仅首次进入时开始'
    : `动作 ${transition.refreshRule.actionCode} 投影时重置`;

export const deriveTimeDrivenTransitionPreviews = (
  model: EquipmentStateClassModel,
): EquipmentStateTimeDrivenTransitionPreview[] => model.attributes.flatMap((attribute) => (
  (attribute.timeDrivenTransitions ?? []).map((transition, index) => ({
    key: `${attribute.code}-time-${index}`,
    attributeCode: attribute.code,
    sourceValueCode: transition.sourceValueCode,
    targetValueCode: transition.targetValueCode,
    startLabel: formatTimeDrivenTransitionStart(transition),
    refreshLabel: formatTimeDrivenTransitionRefresh(transition),
    expiryLabel: formatTemporalExpression(transition.expiryRule.expression),
  }))
));

export type EquipmentCycleLinkageRuleRole = 'START' | 'REFRESH' | 'EXPIRY';

export interface EquipmentCycleLinkagePreview {
  key: string;
  policyCode: string;
  policyName: string;
  ruleRole: EquipmentCycleLinkageRuleRole;
  eventKind: 'STATE_ENTRY' | 'ACTION_STATUS';
  eventReference: string;
  linkedDefinition: string;
  linked: boolean;
}

const isStateModelCycleEvent = (
  event: EquipmentCycleEventReference,
): event is Extract<EquipmentCycleEventReference, { kind: 'STATE_ENTRY' | 'ACTION_STATUS' }> => (
  event.kind === 'STATE_ENTRY' || event.kind === 'ACTION_STATUS'
);

const collectExpiryStateModelEvents = (
  rule: EquipmentCycleExpiryRule,
  path = 'expiry',
): Array<{
  path: string;
  event: Extract<EquipmentCycleEventReference, { kind: 'STATE_ENTRY' | 'ACTION_STATUS' }>;
}> => {
  if (rule.kind === 'EVENT' && isStateModelCycleEvent(rule.event)) {
    return [{ path, event: rule.event }];
  }
  if (rule.kind !== 'COMPOSITE') return [];
  return rule.rules.flatMap((child, index) => collectExpiryStateModelEvents(child, `${path}-${index}`));
};

/**
 * Derives same-model links without creating a second editable definition or any
 * runtime occurrence. Unresolved references remain visible for diagnosis.
 */
export const deriveCycleLinkagePreviews = (
  model: EquipmentStateClassModel,
): EquipmentCycleLinkagePreview[] => model.cyclePolicies.flatMap((policy) => {
  const references: Array<{
    path: string;
    ruleRole: EquipmentCycleLinkageRuleRole;
    event: Extract<EquipmentCycleEventReference, { kind: 'STATE_ENTRY' | 'ACTION_STATUS' }>;
  }> = [];

  if (policy.startRule.kind === 'EVENT' && isStateModelCycleEvent(policy.startRule.event)) {
    references.push({ path: 'start', ruleRole: 'START', event: policy.startRule.event });
  }
  if (policy.refreshRule.kind === 'RESET_ON_EVENT' && isStateModelCycleEvent(policy.refreshRule.event)) {
    references.push({ path: 'refresh', ruleRole: 'REFRESH', event: policy.refreshRule.event });
  }
  collectExpiryStateModelEvents(policy.expiryRule).forEach(({ path, event }) => {
    references.push({ path, ruleRole: 'EXPIRY', event });
  });

  return references.map(({ path, ruleRole, event }) => {
    if (event.kind === 'ACTION_STATUS') {
      const action = model.actions.find((item) => item.actionCode === event.actionCode);
      return {
        key: `${policy.code}-${path}-${event.kind}-${event.actionCode}-${event.status}`,
        policyCode: policy.code,
        policyName: policy.name,
        ruleRole,
        eventKind: event.kind,
        eventReference: `${event.actionCode} · ${event.status === 'RELEASED' ? '已放行' : '已完成'}`,
        linkedDefinition: action
          ? `${action.name} · ${action.effects.map(formatEffect).join('；') || '无效果'}`
          : `未找到动作 ${event.actionCode}`,
        linked: Boolean(action),
      };
    }

    const producers = model.actions.filter((action) => action.effects.some((effect) => (
      effect.effectType === 'SET_STATE'
      && effect.attributeCode === event.attributeCode
      && String(effect.value ?? '') === event.valueCode
    )));
    return {
      key: `${policy.code}-${path}-${event.kind}-${event.attributeCode}-${event.valueCode}`,
      policyCode: policy.code,
      policyName: policy.name,
      ruleRole,
      eventKind: event.kind,
        eventReference: `${displayAttributeCode(event.attributeCode)} / ${event.valueCode}`,
        linkedDefinition: producers.length > 0
          ? `由动作 ${producers.map((action) => action.actionCode).join('、')} 进入`
          : `未找到进入 ${displayAttributeCode(event.attributeCode)} / ${event.valueCode} 的动作`,
      linked: producers.length > 0,
    };
  });
});

const duplicateCodes = <T>(items: T[], codeOf: (item: T) => string): string[] => {
  const counts = new Map<string, number>();
  items.forEach((item) => {
    const code = codeOf(item).trim().toUpperCase();
    counts.set(code, (counts.get(code) ?? 0) + 1);
  });
  return Array.from(counts.entries()).filter(([, count]) => count > 1).map(([code]) => code);
};

const checkAttribute = (attribute: EquipmentStateAttribute): LocalModelProblem[] => {
  const problems: LocalModelProblem[] = [];
  const key = `attribute-${attribute.code || 'new'}`;
  const attributeLabel = displayAttributeCode(attribute.code);
  if (!attribute.code.trim()) problems.push({ key: `${key}-code`, message: '属性编码不能为空。' });
  if (!attribute.name.trim()) problems.push({ key: `${key}-name`, message: `属性 ${attributeLabel} 缺少名称。` });
  if (attribute.kind === 'DISCRETE') {
    if (attribute.values.length === 0) {
      problems.push({ key: `${key}-values`, message: `离散属性 ${attributeLabel} 至少需要一个状态值。` });
    }
    if (duplicateCodes(attribute.values, (value) => value.code).length > 0) {
      problems.push({ key: `${key}-duplicate-values`, message: `属性 ${attributeLabel} 的状态值编码不可重复。` });
    }
    if (attribute.values.filter((value) => value.isInitial).length !== 1) {
      problems.push({ key: `${key}-initial`, message: `离散属性 ${attributeLabel} 必须且只能有一个初始值。` });
    }
  }
  if (attribute.kind === 'COUNTER' && (!attribute.unit?.trim() || attribute.initialNumber == null)) {
    problems.push({ key: `${key}-unit`, message: `计数属性 ${attributeLabel} 需要计量单位和初始值。` });
  }
  if (attribute.kind === 'CLOCK' && (!attribute.clockKind || !attribute.temporalExpression
    || attribute.temporalExpression.value <= 0)) {
    problems.push({ key: `${key}-clock`, message: `时钟属性 ${attributeLabel} 需要时钟类型和有效周期表达式。` });
  }
  if (attribute.kind === 'CLOCK') {
    problems.push(...validateTemporalExpression(attribute.temporalExpression, `时钟属性 ${attributeLabel}`));
  } else if (attribute.temporalExpression) {
    problems.push({ key: `${key}-temporal-unused`, message: `非时钟属性 ${attributeLabel} 不能携带周期表达式。` });
  }
  const transition = attribute.temporalTransition;
  if (transition) {
    if (attribute.kind !== 'DISCRETE') {
      problems.push({ key: `${key}-temporal-kind`, message: `周期状态 ${attributeLabel} 必须是离散状态属性。` });
    }
    if (attribute.values.length !== 2) {
      problems.push({
        key: `STATE_TEMPORAL_STATE_VALUE_CARDINALITY_INVALID-${key}`,
        message: `STATE_TEMPORAL_STATE_VALUE_CARDINALITY_INVALID：周期状态 ${attributeLabel} 必须且只能声明有效值和到期值两个状态。`,
      });
    }
    const valueCodes = new Set(attribute.values.map((value) => value.code.trim().toUpperCase()));
    if (!valueCodes.has(transition.validValueCode) || !valueCodes.has(transition.expiredValueCode)
      || transition.validValueCode === transition.expiredValueCode) {
      problems.push({
        key: `${key}-temporal-values`,
        message: `周期状态 ${attributeLabel} 的有效值和到期值必须是两个不同的已声明状态值。`,
      });
    }
    const initial = attribute.values.find((value) => value.isInitial)?.code.trim().toUpperCase();
    if (initial !== transition.expiredValueCode) {
      problems.push({
        key: `${key}-temporal-initial`,
        message: `周期状态 ${attributeLabel} 的初始值必须为到期值 ${transition.expiredValueCode}。`,
      });
    }
    problems.push(...validateTemporalExpression(
      transition.expiryRule.expression,
      `周期状态 ${attributeLabel} 的过期计算方式`,
    ));
    const template = transition.enforcement.violationMessageTemplate;
    const hasResource = ['{resourceCode}', '{resourceId}'].some((field) => template.includes(field));
    const hasExpiry = ['{dueAt}', '{expiredAt}', '{timeWindow}'].some((field) => template.includes(field));
    if ((!transition.enforcement.constraintCode.trim() && !isAutoAttributeCode(attribute.code))
      || !hasResource || !hasExpiry) {
      problems.push({
        key: `${key}-temporal-enforcement`,
        message: `周期状态 ${attributeLabel} 的约束编码和诊断模板必须包含资源及到期时刻占位符。`,
      });
    }
  }
  return problems;
};

const normalizedCode = (value: string | null | undefined): string => value?.trim().toUpperCase() ?? '';

const activationEffectsFor = (
  action: EquipmentStateAction,
  attributeCode: string,
): EquipmentStateEffect[] => action.effects.filter((effect) => (
  effect.effectType === 'ACTIVATE_TEMPORAL_STATE'
  && normalizedCode(effect.attributeCode) === normalizedCode(attributeCode)
));

const activationActionsFor = (
  actions: EquipmentStateAction[],
  attributeCode: string,
  actionCode?: string,
): EquipmentStateAction[] => {
  const expectedActionCode = normalizedCode(actionCode);
  return actions.filter((action) => (
    (!expectedActionCode || normalizedCode(action.actionCode) === expectedActionCode)
    && activationEffectsFor(action, attributeCode).length > 0
  ));
};

const singleActivationActionsFor = (
  actions: EquipmentStateAction[],
  attributeCode: string,
  actionCode?: string,
): EquipmentStateAction[] => activationActionsFor(actions, attributeCode, actionCode).filter((action) => (
  activationEffectsFor(action, attributeCode).length === 1
  && activationEffectsFor(action, attributeCode)[0].value === undefined
));

const isMatchingLegacyActionEvent = (
  event: EquipmentStateTemporalTransition['startRule']['event'],
  actionCodes: Set<string>,
): boolean => event.kind === 'ACTION_EFFECT'
  && (!normalizedCode(event.actionCode) || actionCodes.has(normalizedCode(event.actionCode)));

/**
 * Converts only compatible old ACTION_EFFECT + ACTIVATE_TEMPORAL_STATE pairs
 * while a writable draft is being saved. Read payloads remain untouched.
 */
export const normalizeLegacyTemporalDraft = (
  model: EquipmentStateClassModel,
  attributeCodes?: string[],
): EquipmentStateClassModel => {
  const selectedAttributeCodes = attributeCodes
    ? new Set(attributeCodes.map(normalizedCode))
    : null;
  const migrations = new Map<string, { actionCodes: Set<string>; validValueCode: string }>();

  model.attributes.forEach((attribute) => {
    const transition = attribute.temporalTransition;
    const attributeCode = normalizedCode(attribute.code);
    if (!transition || (selectedAttributeCodes && !selectedAttributeCodes.has(attributeCode))) return;
    const startEvent = transition.startRule.event;
    if (startEvent.kind !== 'ACTION_EFFECT') return;
    const startActions = singleActivationActionsFor(model.actions, attribute.code, startEvent.actionCode);
    if (startActions.length === 0) return;
    const refreshActions = transition.refreshRule.kind === 'RESET_ON_EVENT'
      && transition.refreshRule.event.kind === 'ACTION_EFFECT'
      ? singleActivationActionsFor(model.actions, attribute.code, transition.refreshRule.event.actionCode)
      : [];
    const matchedActions = [...startActions, ...refreshActions]
      .filter((action, index, all) => (
        all.findIndex((item) => normalizedCode(item.actionCode) === normalizedCode(action.actionCode)) === index
      ));
    migrations.set(attributeCode, {
      actionCodes: new Set(matchedActions.map((action) => normalizedCode(action.actionCode))),
      validValueCode: normalizedCode(transition.validValueCode),
    });
  });

  if (migrations.size === 0) return model;

  return {
    ...model,
    attributes: model.attributes.map((attribute) => {
      const migration = migrations.get(normalizedCode(attribute.code));
      const transition = attribute.temporalTransition;
      if (!migration || !transition) return attribute;
      const refreshRule = transition.refreshRule.kind === 'RESET_ON_EVENT'
        && isMatchingLegacyActionEvent(transition.refreshRule.event, migration.actionCodes)
        ? { kind: 'RESET_ON_START_EVENT' as const }
        : transition.refreshRule;
      return {
        ...attribute,
        temporalTransition: {
          ...transition,
          startRule: {
            kind: 'EVENT',
            event: {
              kind: 'STATE_ENTRY',
              attributeCode: normalizedCode(attribute.code),
              valueCode: migration.validValueCode,
            },
          },
          refreshRule,
        },
      };
    }),
    actions: model.actions.map((action) => ({
      ...action,
      effects: action.effects.map((effect) => {
        const migration = migrations.get(normalizedCode(effect.attributeCode));
        if (effect.effectType !== 'ACTIVATE_TEMPORAL_STATE' || !migration
          || !migration.actionCodes.has(normalizedCode(action.actionCode))) {
          return effect;
        }
        return {
          attributeCode: effect.attributeCode,
          effectType: 'SET_STATE' as const,
          value: migration.validValueCode,
        };
      }),
    })),
  };
};

const checkTemporalTransitionReferences = (
  attribute: EquipmentStateAttribute,
  attributesByCode: Map<string, EquipmentStateAttribute>,
  actions: EquipmentStateAction[],
  readyConditions: EquipmentStateCondition[],
): LocalModelProblem[] => {
  const transition = attribute.temporalTransition;
  if (!transition) return [];
  const key = `attribute-${attribute.code || 'new'}-temporal`;
  const attributeLabel = displayAttributeCode(attribute.code);
  const problems: LocalModelProblem[] = [];
  const startEvent = transition.startRule.event;
  if (startEvent.kind === 'STATE_ENTRY') {
    const isSelf = normalizedCode(startEvent.attributeCode) === normalizedCode(attribute.code);
    const isValidValue = normalizedCode(startEvent.valueCode) === normalizedCode(transition.validValueCode);
    if (!isSelf) {
      problems.push({
        key: `STATE_TEMPORAL_STATE_DIRECT_START_REQUIRED-${key}`,
        message: `STATE_TEMPORAL_STATE_DIRECT_START_REQUIRED：周期状态 ${attributeLabel} 的记时开始方式必须是本属性进入有效值。`,
      });
    } else if (!isValidValue) {
      problems.push({
        key: `STATE_TEMPORAL_STATE_SELF_ENTRY_INVALID-${key}`,
        message: `STATE_TEMPORAL_STATE_SELF_ENTRY_INVALID：周期状态 ${attributeLabel} 的自引用开始值必须是有效值 ${transition.validValueCode}。`,
      });
    }
  } else if (startEvent.kind === 'ACTION_EFFECT') {
    if (singleActivationActionsFor(actions, attribute.code, startEvent.actionCode).length === 0) {
      problems.push({
        key: `STATE_TEMPORAL_STATE_LEGACY_TRIGGER_UNPAIRED-${key}`,
        message: `STATE_TEMPORAL_STATE_LEGACY_TRIGGER_UNPAIRED：周期状态 ${attributeLabel} 的旧版 ACTION_EFFECT 没有匹配的激活效果，无法安全迁移。`,
      });
    }
  } else {
    problems.push({
      key: `STATE_TEMPORAL_STATE_DIRECT_START_REQUIRED-${key}`,
      message: `STATE_TEMPORAL_STATE_DIRECT_START_REQUIRED：周期状态 ${attributeLabel} 的记时开始方式只能是本属性进入有效值。`,
    });
  }

  if (transition.refreshRule.kind === 'RESET_ON_EVENT') {
    const event = transition.refreshRule.event;
    const invalidSelfRefreshEntry = event.kind === 'STATE_ENTRY'
      && normalizedCode(event.attributeCode) === normalizedCode(attribute.code)
      && normalizedCode(event.valueCode) !== normalizedCode(transition.validValueCode);
    const invalidRefreshEvent = event.kind === 'ACTION_EFFECT'
      ? singleActivationActionsFor(actions, attribute.code, event.actionCode).length === 0
      : event.kind === 'STATE_ENTRY'
        ? !attributesByCode.get(event.attributeCode)?.values.some((value) => (
          normalizedCode(value.code) === normalizedCode(event.valueCode)
        ))
        : event.kind === 'ACTION_STATUS'
          ? !actions.some((action) => normalizedCode(action.actionCode) === normalizedCode(event.actionCode))
          : false;
    if (invalidSelfRefreshEntry) {
      problems.push({
        key: `STATE_TEMPORAL_STATE_SELF_ENTRY_INVALID-${key}-refresh`,
        message: `STATE_TEMPORAL_STATE_SELF_ENTRY_INVALID：周期状态 ${attributeLabel} 的自引用刷新值必须是有效值 ${transition.validValueCode}。`,
      });
    } else if (invalidRefreshEvent) {
      problems.push({
        key: `STATE_TEMPORAL_STATE_REFRESH_RULE_INVALID-${key}`,
        message: `STATE_TEMPORAL_STATE_REFRESH_RULE_INVALID：周期状态 ${attributeLabel} 的刷新方式只能由写入有效值自动重置。`,
      });
    }
  }
  if (transition.enforcement.hardOrSoft === 'hard' && !readyConditions.some((condition) => (
    condition.attributeCode === attribute.code
    && condition.operator === 'EQ'
    && condition.value === transition.validValueCode
  ))) {
    problems.push({
      key: `${key}-ready-guard`,
      message: `硬周期状态 ${attributeLabel} 需要就绪条件 ${attributeLabel} EQ ${transition.validValueCode}。`,
    });
  }
  return problems;
};

const actionWritesState = (
  action: EquipmentStateAction,
  attributeCode: string,
  valueCode: string,
): boolean => action.effects.some((effect) => (
  effect.effectType === 'SET_STATE'
  && normalizedCode(effect.attributeCode) === normalizedCode(attributeCode)
  && normalizedCode(String(effect.value ?? '')) === normalizedCode(valueCode)
));

const checkTimeDrivenTransitions = (
  attribute: EquipmentStateAttribute,
  actions: EquipmentStateAction[],
): LocalModelProblem[] => {
  const transitions = attribute.timeDrivenTransitions ?? [];
  if (!transitions.length) return [];
  const key = `attribute-${attribute.code || 'new'}-time-driven`;
  const attributeLabel = displayAttributeCode(attribute.code);
  const problems: LocalModelProblem[] = [];
  if (attribute.kind !== 'DISCRETE') {
    return [{
      key: `${key}-kind`,
      message: `多状态时间边只能配置在离散状态属性 ${attributeLabel} 上。`,
    }];
  }
  if (attribute.temporalTransition) {
    problems.push({
      key: `${key}-legacy-conflict`,
      message: `属性 ${attributeLabel} 不能同时使用两态周期状态和多状态时间边。`,
    });
  }
  const valueCodes = new Set(attribute.values.map((value) => normalizedCode(value.code)));
  const sources = new Set<string>();
  transitions.forEach((transition, index) => {
    const transitionKey = `${key}-${index}`;
    const source = normalizedCode(transition.sourceValueCode);
    const target = normalizedCode(transition.targetValueCode);
    if (sources.has(source)) {
      problems.push({
        key: `${transitionKey}-source-duplicate`,
        message: `状态 ${attributeLabel} 的 ${transition.sourceValueCode} 已有一个自动到期去向；同一来源状态只能维护一条时间边。`,
      });
    }
    sources.add(source);
    if (!source || !target || source === target || !valueCodes.has(source) || !valueCodes.has(target)) {
      problems.push({
        key: `${transitionKey}-values`,
        message: `状态 ${attributeLabel} 的时间边必须引用两个不同的已声明状态值。`,
      });
    }
    problems.push(...validateTemporalExpression(
      transition.expiryRule.expression,
      `状态 ${attributeLabel} 的 ${transition.sourceValueCode} 到期计算`,
    ));
    if (transition.startRule.kind === 'ACTION_BOUNDARY') {
      const startActionCode = transition.startRule.actionCode;
      const action = actions.find((item) => normalizedCode(item.actionCode) === normalizedCode(startActionCode));
      if (!action || !action.operationId || !actionWritesState(action, attribute.code, transition.sourceValueCode)) {
        problems.push({
          key: `${transitionKey}-start-action`,
          message: `时间边 ${transition.sourceValueCode} 的开始动作必须关联操作库，并在计划边界将 ${attributeLabel} 写为该来源状态。`,
        });
      }
    } else if (!actions.some((action) => (
      Boolean(action.operationId) && actionWritesState(action, attribute.code, transition.sourceValueCode)
    ))) {
      problems.push({
        key: `${transitionKey}-source-writer`,
        message: `时间边 ${transition.sourceValueCode} 缺少关联操作库的直接状态动作；请先配置将 ${attributeLabel} 写为该状态的动作。`,
      });
    }
    if (transition.refreshRule.kind === 'RESET_ON_ACTION_BOUNDARY') {
      const refreshActionCode = transition.refreshRule.actionCode;
      const action = actions.find((item) => normalizedCode(item.actionCode) === normalizedCode(refreshActionCode));
      if (!action || !action.operationId) {
        problems.push({
          key: `${transitionKey}-refresh-action`,
          message: `时间边 ${transition.sourceValueCode} 的刷新动作必须关联操作库。`,
        });
      }
    }
  });
  return problems;
};

const checkLegacyTemporalActivationEffects = (
  actions: EquipmentStateAction[],
  attributesByCode: Map<string, EquipmentStateAttribute>,
): LocalModelProblem[] => {
  const problems: LocalModelProblem[] = [];
  actions.forEach((action) => {
    action.effects.forEach((effect, index) => {
      if (effect.effectType !== 'ACTIVATE_TEMPORAL_STATE') return;
      const attribute = attributesByCode.get(effect.attributeCode);
      const key = `action-${action.actionCode || 'new'}-legacy-activation-${index}`;
      if (!attribute || attribute.kind !== 'DISCRETE' || !attribute.temporalTransition) {
        problems.push({
          key: `STATE_TEMPORAL_STATE_ACTIVATION_TARGET_INVALID-${key}`,
          message: `STATE_TEMPORAL_STATE_ACTIVATION_TARGET_INVALID：旧版激活效果 ${action.actionCode} 未指向周期状态属性。`,
        });
        return;
      }
      const startEvent = attribute.temporalTransition.startRule.event;
      if (startEvent.kind !== 'ACTION_EFFECT'
        || (normalizedCode(startEvent.actionCode)
          && normalizedCode(startEvent.actionCode) !== normalizedCode(action.actionCode))
        || activationEffectsFor(action, attribute.code).length !== 1) {
        problems.push({
          key: `STATE_TEMPORAL_STATE_LEGACY_ACTIVATION_UNPAIRED-${key}`,
          message: `STATE_TEMPORAL_STATE_LEGACY_ACTIVATION_UNPAIRED：旧版激活效果 ${action.actionCode} 与周期状态 ${attribute.code} 的开始事件不匹配。`,
        });
      }
      if (effect.value !== undefined) {
        problems.push({
          key: `STATE_TEMPORAL_STATE_ACTIVATION_VALUE_FORBIDDEN-${key}`,
          message: `STATE_TEMPORAL_STATE_ACTIVATION_VALUE_FORBIDDEN：旧版激活效果 ${action.actionCode} 不能携带目标状态值。`,
        });
      }
    });
  });
  return problems;
};

const checkAction = (
  action: EquipmentStateAction,
  attributesByCode: Map<string, EquipmentStateAttribute>,
): LocalModelProblem[] => {
  const problems: LocalModelProblem[] = [];
  const key = `action-${action.actionCode || 'new'}`;
  if (!action.actionCode.trim()) problems.push({ key: `${key}-code`, message: '动作编码不能为空。' });
  if (!action.name.trim()) problems.push({ key: `${key}-name`, message: `动作 ${action.actionCode || '（未编码）'} 缺少名称。` });
  if (action.effects.length === 0) {
    problems.push({ key: `${key}-effects`, message: `动作 ${action.actionCode} 至少需要一个发生后状态变化。` });
  }
  [...action.guards, ...action.effects].forEach((item, index) => {
    if (!attributesByCode.has(item.attributeCode)) {
      problems.push({
        key: `${key}-reference-${index}`,
        message: `动作 ${action.actionCode} 引用了不存在的属性 ${displayAttributeCode(item.attributeCode)}。`,
      });
    }
  });
  const duplicateEffectTargets = duplicateCodes(action.effects, (effect) => effect.attributeCode);
  if (duplicateEffectTargets.length > 0) {
    problems.push({
      key: `${key}-duplicate-effect-targets`,
      message: `动作 ${action.actionCode} 对同一属性重复定义效果：${duplicateEffectTargets.join('、')}。`,
    });
  }
  action.guards.forEach((guard, index) => {
    const attribute = attributesByCode.get(guard.attributeCode);
    if (!attribute) return;
    const allowed = attribute.kind === 'DISCRETE'
      ? ['EQ', 'NE', 'IN', 'NOT_IN']
      : attribute.kind === 'COUNTER'
        ? ['EQ', 'NE', 'LT', 'LTE', 'GT', 'GTE']
        : ['IS_SET', 'IS_NOT_SET', 'NOT_EXPIRED', 'EXPIRED'];
    if (!allowed.includes(guard.operator)) {
      problems.push({
        key: `${key}-guard-kind-${index}`,
        message: `动作 ${action.actionCode} 的 ${displayAttributeCode(guard.attributeCode)} 守卫运算符与属性类型不兼容。`,
      });
    }
    if (conditionNeedsValue(guard.operator) && (guard.value == null || guard.value === '')) {
      problems.push({ key: `${key}-guard-value-${index}`, message: `动作 ${action.actionCode} 的守卫 ${index + 1} 缺少比较值。` });
    }
  });
  action.effects.forEach((effect, index) => {
    const attribute = attributesByCode.get(effect.attributeCode);
    if (attribute) {
      const allowed = attribute.kind === 'DISCRETE'
        ? attribute.temporalTransition ? ['SET_STATE', 'ACTIVATE_TEMPORAL_STATE'] : ['SET_STATE']
        : attribute.kind === 'COUNTER'
          ? ['INCREMENT', 'RESET_COUNTER']
          : ['START_TIMER', 'CLEAR_TIMER'];
      if (!allowed.includes(effect.effectType)) {
        problems.push({
          key: `${key}-effect-kind-${index}`,
          message: `动作 ${action.actionCode} 的 ${displayAttributeCode(effect.attributeCode)} 效果与属性类型不兼容。`,
        });
      }
      if (effect.effectType === 'SET_STATE'
        && !attribute.values.some((value) => normalizedCode(value.code) === normalizedCode(String(effect.value ?? '')))) {
        problems.push({
          key: `${key}-effect-state-${index}`,
          message: `动作 ${action.actionCode} 设置了不存在的状态值 ${String(effect.value ?? '（空）')}。`,
        });
      }
      if (effect.effectType === 'SET_STATE' && attribute.temporalTransition) {
        const transition = attribute.temporalTransition;
        const stateValue = normalizedCode(String(effect.value ?? ''));
        const validValue = normalizedCode(transition.validValueCode);
        const expiredValue = normalizedCode(transition.expiredValueCode);
        if (stateValue !== validValue && stateValue !== expiredValue) {
          problems.push({
            key: `STATE_TEMPORAL_STATE_DIRECT_VALUE_INVALID-${key}-${index}`,
            message: `STATE_TEMPORAL_STATE_DIRECT_VALUE_INVALID：动作 ${action.actionCode} 对周期状态 ${displayAttributeCode(attribute.code)} 只能写入有效值 ${transition.validValueCode} 或到期值 ${transition.expiredValueCode}。`,
          });
        }
        if (!action.operationId) {
          problems.push({
            key: `STATE_TEMPORAL_STATE_DIRECT_OPERATION_REQUIRED-${key}-${index}`,
            message: `STATE_TEMPORAL_STATE_DIRECT_OPERATION_REQUIRED：动作 ${action.actionCode} 直接写入周期状态前必须关联操作库来源。`,
          });
        }
      }
    }
    if ((effect.effectType === 'SET_STATE' || effect.effectType === 'INCREMENT') && effect.value == null) {
      problems.push({ key: `${key}-effect-value-${index}`, message: `动作 ${action.actionCode} 的效果 ${index + 1} 缺少值。` });
    }
    if (effect.effectType === 'ACTIVATE_TEMPORAL_STATE' && effect.value !== undefined) {
      problems.push({
        key: `STATE_TEMPORAL_STATE_ACTIVATION_VALUE_FORBIDDEN-${key}-${index}`,
        message: `STATE_TEMPORAL_STATE_ACTIVATION_VALUE_FORBIDDEN：动作 ${action.actionCode} 的“激活周期状态”效果不能填写目标值。`,
      });
    }
    if (effect.effectType === 'INCREMENT' && Number(effect.value) === 0) {
      problems.push({ key: `${key}-increment-${index}`, message: `动作 ${action.actionCode} 的计数增量不能为 0。` });
    }
  });
  return problems;
};

export const validateEquipmentStateModelLocally = (
  model: EquipmentStateClassModel,
): LocalModelProblem[] => {
  const problems = model.attributes.flatMap(checkAttribute);
  const attributesByCode = new Map(model.attributes.map((attribute) => [attribute.code, attribute]));
  const duplicateAttributes = duplicateCodes(model.attributes, (attribute) => attribute.code);
  const duplicateActions = duplicateCodes(model.actions, (action) => action.actionCode);
  if (duplicateAttributes.length > 0) {
    problems.push({
      key: 'duplicate-attributes',
      message: `属性编码重复：${duplicateAttributes.map(displayAttributeCode).join('、')}。`,
    });
  }
  if (duplicateActions.length > 0) {
    problems.push({ key: 'duplicate-actions', message: `动作编码重复：${duplicateActions.join('、')}。` });
  }
  const semanticAttributes = model.attributes.filter((attribute) => (
    attribute.values.some((value) => Boolean(value.semanticState))
  ));
  if (semanticAttributes.length > 1) {
    problems.push({
      key: 'semantic-state-multiple-attributes',
      message: `标准运行语义只能配置在一条离散属性上；当前同时存在于 ${semanticAttributes.map((item) => displayAttributeCode(item.code)).join('、')}。`,
    });
  }
  problems.push(...model.attributes.flatMap((attribute) => checkTemporalTransitionReferences(
    attribute,
    attributesByCode,
    model.actions,
    model.readyConditions,
  )));
  problems.push(...model.attributes.flatMap((attribute) => checkTimeDrivenTransitions(
    attribute,
    model.actions,
  )));
  problems.push(...model.actions.flatMap((action) => checkAction(
    action,
    attributesByCode,
  )));
  problems.push(...checkLegacyTemporalActivationEffects(model.actions, attributesByCode));
  model.readyConditions.forEach((condition, index) => {
    const attribute = attributesByCode.get(condition.attributeCode);
    if (!attribute) {
      problems.push({
        key: `ready-${index}`,
        message: `就绪条件引用了不存在的属性 ${displayAttributeCode(condition.attributeCode)}。`,
      });
      return;
    }
    const allowed = attribute.kind === 'DISCRETE'
      ? ['EQ', 'NE', 'IN', 'NOT_IN']
      : attribute.kind === 'COUNTER'
        ? ['EQ', 'NE', 'LT', 'LTE', 'GT', 'GTE']
        : ['IS_SET', 'IS_NOT_SET', 'NOT_EXPIRED', 'EXPIRED'];
    if (!allowed.includes(condition.operator)) {
      problems.push({ key: `ready-kind-${index}`, message: `就绪条件 ${index + 1} 的运算符与属性类型不兼容。` });
    }
    if (conditionNeedsValue(condition.operator) && (condition.value == null || condition.value === '')) {
      problems.push({ key: `ready-value-${index}`, message: `就绪条件 ${index + 1} 缺少比较值。` });
    }
  });
  const duplicatePolicies = duplicateCodes(model.cyclePolicies, (policy) => policy.code);
  if (duplicatePolicies.length > 0) {
    problems.push({ key: 'duplicate-cycle-policies', message: `周期策略编码重复：${duplicatePolicies.join('、')}。` });
  }
  model.cyclePolicies.forEach((policy, index) => {
    problems.push(...validateCyclePolicyLocally(policy).map((problem) => ({
      key: `policy-${index}-${problem.key}`,
      message: problem.message,
    })));
  });
  const temporalConstraints = new Map<string, string>();
  model.attributes.forEach((attribute) => {
    const transition = attribute.temporalTransition;
    if (!transition) return;
    const constraintCode = transition.enforcement.constraintCode.trim().toUpperCase();
    if (!constraintCode) return;
    const existing = temporalConstraints.get(constraintCode);
    if (existing) {
      problems.push({
        key: `temporal-constraint-${attribute.code}`,
        message: `周期状态 ${displayAttributeCode(existing)} 与 ${displayAttributeCode(attribute.code)} 复用了约束编码 ${constraintCode}。`,
      });
      return;
    }
    temporalConstraints.set(constraintCode, attribute.code);
  });
  model.cyclePolicies.forEach((policy) => {
    const constraintCode = policy.enforcement.constraintCode.trim().toUpperCase();
    const owner = temporalConstraints.get(constraintCode);
    if (owner) {
      problems.push({
        key: `temporal-policy-constraint-${policy.code}`,
        message: `独立周期约束 ${policy.code} 不能复用周期状态 ${owner} 的约束编码 ${constraintCode}。`,
      });
    }
  });
  return problems;
};

const validateTemporalExpression = (
  expression: EquipmentTemporalExpression | null,
  label: string,
  policyTimeZone?: string,
): LocalModelProblem[] => {
  if (!expression) return [];
  const problems: LocalModelProblem[] = [];
  if (!Number.isFinite(expression.value) || expression.value <= 0
    || (expression.basis === 'CALENDAR_PERIOD' && !Number.isInteger(expression.value))) {
    problems.push({
      key: `${label}-value`,
      message: expression.basis === 'CALENDAR_PERIOD'
        ? `${label}的日历期间数值必须是大于 0 的整数。`
        : `${label}的连续时长数值必须大于 0。`,
    });
  }
  if (expression.basis === 'CALENDAR_PERIOD') {
    if (!expression.timeZone.trim()) {
      problems.push({ key: `${label}-timezone`, message: `${label}需要 IANA 时区。` });
    }
    if (policyTimeZone && expression.timeZone !== policyTimeZone) {
      problems.push({ key: `${label}-timezone-match`, message: `${label}时区必须与策略时区 ${policyTimeZone} 一致。` });
    }
    if (['MONTH', 'QUARTER', 'YEAR'].includes(expression.unit) && !expression.monthEndPolicy) {
      problems.push({ key: `${label}-month-end`, message: `${label}使用月/季度/年时必须选择月末策略。` });
    }
    if (['DAY', 'WEEK'].includes(expression.unit) && expression.monthEndPolicy) {
      problems.push({ key: `${label}-month-end-unused`, message: `${label}使用日历天/周时月末策略必须为空。` });
    }
  }
  return problems;
};

const RFC3339_WITH_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

const validateCycleTimeExpression = (
  expression: EquipmentCycleTimeExpression | null,
  label: string,
): LocalModelProblem[] => {
  if (!expression) return [];
  const problems: LocalModelProblem[] = [];
  if (!Number.isFinite(expression.value) || expression.value <= 0
    || (expression.basis === 'CALENDAR_PERIOD' && !Number.isInteger(expression.value))) {
    problems.push({
      key: `${label}-value`,
      message: expression.basis === 'CALENDAR_PERIOD'
        ? `${label}的日历周期必须是大于 0 的整数。`
        : `${label}的连续时长必须大于 0。`,
    });
  }
  if (expression.basis === 'CALENDAR_PERIOD') {
    if (['MONTH', 'QUARTER', 'YEAR'].includes(expression.unit) && !expression.monthEndPolicy) {
      problems.push({ key: `${label}-month-end`, message: `${label}使用月、季度或年时必须选择月末处理。` });
    }
    if (['DAY', 'WEEK'].includes(expression.unit) && expression.monthEndPolicy) {
      problems.push({ key: `${label}-month-end-unused`, message: `${label}使用自然日或自然周时不应设置月末处理。` });
    }
  }
  return problems;
};

const validateCycleEvent = (
  event: EquipmentCycleEventReference,
  label: string,
): LocalModelProblem[] => {
  if ((event.kind === 'ACTION_STATUS' && !event.actionCode.trim())
    || (event.kind === 'STATE_ENTRY' && (!event.attributeCode.trim() || !event.valueCode.trim()))
    || (event.kind === 'METER_RESET' && !event.meterCode.trim())
    || (event.kind === 'CUSTOM_EVENT' && !event.eventCode.trim())) {
    return [{ key: `${label}-event`, message: `${label}缺少对应的状态、动作、仪表或事件编码。` }];
  }
  return [];
};

const isRollableCycleRule = (rule: EquipmentCycleExpiryRule): boolean => (
  ['ELAPSED', 'CALENDAR', 'SCHEDULE'].includes(rule.kind)
  || (rule.kind === 'COMPOSITE' && rule.rules.every(isRollableCycleRule))
);

const validateCycleRule = (
  rule: EquipmentCycleExpiryRule,
  baselineByKey: Map<string, EquipmentCyclePolicy['startRule']['baselineCaptures'][number]>,
  depth = 1,
): LocalModelProblem[] => {
  const problems: LocalModelProblem[] = [];
  if (depth > 3) {
    return [{ key: 'composite-depth', message: '组合规则最多嵌套 3 层。' }];
  }
  if (rule.kind === 'ELAPSED') return validateCycleTimeExpression(rule.duration, '连续到期时长');
  if (rule.kind === 'CALENDAR') return validateCycleTimeExpression(rule.period, '自然日历周期');
  if (rule.kind === 'ABSOLUTE_DATE_TIME') {
    return RFC3339_WITH_OFFSET.test(rule.fixedDateTime)
      ? []
      : [{ key: 'absolute-rfc3339', message: '固定截止时刻必须是带时区偏移的完整时间。' }];
  }
  if (rule.kind === 'SCHEDULE') {
    if (rule.schedule.kind === 'BUSINESS_INTERVAL') {
      if (!Number.isInteger(rule.schedule.value) || rule.schedule.value <= 0
        || !rule.schedule.businessCalendarCode.trim()) {
        problems.push({ key: 'business-rule', message: '业务日历间隔需要正整数和业务日历编码。' });
      }
      return problems;
    }
    if (!/^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(rule.schedule.localTime)) {
      problems.push({ key: 'pattern-time', message: '日历模式时间必须使用 HH:mm:ss。' });
    }
    const pattern = rule.schedule.pattern;
    if (pattern.kind === 'WEEKLY' && pattern.weekdays.length === 0) {
      problems.push({ key: 'pattern-weekdays', message: '每周模式至少选择一个星期。' });
    }
    if (pattern.kind === 'WEEKLY'
      && (!Number.isInteger(pattern.intervalWeeks) || pattern.intervalWeeks <= 0)) {
      problems.push({ key: 'pattern-weeks', message: '每周模式的间隔周数必须是正整数。' });
    }
    if (pattern.kind === 'MONTHLY_DAY'
      && (!Number.isInteger(pattern.intervalMonths) || pattern.intervalMonths <= 0
        || !Number.isInteger(pattern.dayOfMonth) || pattern.dayOfMonth < 1 || pattern.dayOfMonth > 31)) {
      problems.push({ key: 'pattern-month-day', message: '每月日期模式需要正整数间隔月数和 1–31 的日期。' });
    }
    if (pattern.kind === 'MONTHLY_NTH_WEEKDAY'
      && (!Number.isInteger(pattern.intervalMonths) || pattern.intervalMonths <= 0)) {
      problems.push({ key: 'pattern-month-weekday', message: '每月星期模式的间隔月数必须是正整数。' });
    }
    if (pattern.kind === 'YEARLY_DATE'
      && (!Number.isInteger(pattern.intervalYears) || pattern.intervalYears <= 0
        || pattern.month < 1 || pattern.month > 12 || pattern.day < 1 || pattern.day > 31)) {
      problems.push({ key: 'pattern-year-date', message: '每年日期模式需要正整数间隔年数、1–12 月和 1–31 日。' });
    }
    return problems;
  }
  if (rule.kind === 'ACCUMULATED') {
    if (!rule.source.code.trim() || !rule.baselineKey.trim() || rule.delta <= 0 || !rule.unit.trim()) {
      problems.push({ key: 'accumulated-rule', message: '累计判定需要数据来源、启动基线、正增量和单位。' });
    }
    const baseline = baselineByKey.get(rule.baselineKey);
    if (!baseline) {
      problems.push({ key: 'accumulated-baseline', message: `累计判定引用的启动基线 ${rule.baselineKey || '（空）'} 不存在。` });
    } else if (baseline.source.kind === 'EVENT_FIELD'
      || baseline.source.kind !== rule.source.kind
      || baseline.source.code !== rule.source.code) {
      problems.push({ key: 'accumulated-baseline-source', message: '累计判定的数据来源必须与启动时采集基线的数据来源一致。' });
    }
    return problems;
  }
  if (rule.kind === 'THRESHOLD') {
    if (!rule.source.code.trim() || !Number.isFinite(rule.threshold) || !rule.unit.trim()) {
      problems.push({ key: 'threshold-rule', message: '阈值判定需要数据来源、判定值和单位。' });
    }
    return problems;
  }
  if (rule.kind === 'CONDITION') {
    if (!rule.signalCode.trim() || rule.threshold === '') {
      problems.push({ key: 'condition-rule', message: '条件判定需要信号编码和判定值。' });
    }
    if (['LT', 'LTE', 'GT', 'GTE'].includes(rule.operator) && typeof rule.threshold !== 'number') {
      problems.push({ key: 'condition-numeric', message: '数值比较运算符必须使用数值阈值。' });
    }
    problems.push(...validateCycleTimeExpression(rule.sustainFor, '条件持续时间'));
    return problems;
  }
  if (rule.kind === 'EVENT') return validateCycleEvent(rule.event, '到期事件');
  if (rule.rules.length < 2) {
    problems.push({ key: 'composite-members', message: '组合规则至少需要两条子规则。' });
  }
  if (rule.rules.length > 20) {
    problems.push({ key: 'composite-size', message: '每个组合规则最多包含 20 条直接子规则。' });
  }
  rule.rules.forEach((child, index) => {
    problems.push(...validateCycleRule(child, baselineByKey, depth + 1).map((problem) => ({
      key: `child-${index}-${problem.key}`,
      message: problem.message,
    })));
  });
  return problems;
};

export const validateCyclePolicyLocally = (policy: EquipmentCyclePolicy): LocalModelProblem[] => {
  const problems: LocalModelProblem[] = [];
  if (!policy.code.trim() || !policy.name.trim()) {
    problems.push({ key: 'identity', message: '周期策略需要稳定编码和名称。' });
  }
  if (!policy.calculationContext.timeZone.trim()) {
    problems.push({ key: 'timezone', message: `策略 ${policy.code || '（未编码）'} 需要 IANA 时区。` });
  }
  const captures = policy.startRule.baselineCaptures;
  const duplicateBaselineKeys = duplicateCodes(captures, (capture) => capture.key);
  if (duplicateBaselineKeys.length > 0) {
    problems.push({ key: 'baseline-duplicates', message: `启动基线编码重复：${duplicateBaselineKeys.join('、')}。` });
  }
  captures.forEach((capture, index) => {
    if (!capture.key.trim()
      || (capture.source.kind === 'EVENT_FIELD' ? !capture.source.field.trim() : !capture.source.code.trim())) {
      problems.push({ key: `baseline-${index}`, message: `启动基线 ${index + 1} 缺少编码或数据来源。` });
    }
  });
  const baselineByKey = new Map(captures.map((capture) => [capture.key, capture]));
  problems.push(...validateCycleRule(policy.expiryRule, baselineByKey));
  problems.push(...validateCycleTimeExpression(policy.window.warningBefore, '预警提前量'));
  problems.push(...validateCycleTimeExpression(policy.window.dueTolerance, '到期容差'));
  problems.push(...validateCycleTimeExpression(policy.window.gracePeriod, '宽限窗口'));
  if (policy.window.warningBefore && !isPredictableCycleDueRule(policy.expiryRule)) {
    problems.push({
      key: 'warning-unpredictable',
      message: '当前到期判定无法提前计算明确到期时刻，不能设置预警提前量。',
    });
  }
  if (policy.calculationContext.businessDayAdjustment !== 'NONE'
    && !policy.calculationContext.adjustmentCalendarCode?.trim()) {
    problems.push({ key: 'adjustment-calendar', message: '启用到期日工作日调整时必须指定调整日历编码。' });
  }
  if (policy.enforcement.activationPoint === 'GRACE_END' && !policy.window.gracePeriod) {
    problems.push({ key: 'activation-point', message: '选择“宽限结束生效”前必须设置宽限窗口。' });
  }
  if (!policy.enforcement.constraintCode.trim() || !policy.enforcement.violationMessageTemplate.trim()) {
    problems.push({ key: 'enforcement', message: '诊断契约需要 constraint_code 和违规消息模板。' });
  }
  const template = policy.enforcement.violationMessageTemplate;
  const hasTimeBoundary = ['{dueAt}', '{graceEnd}', '{timeWindow}'].some((field) => template.includes(field));
  const hasResource = ['{resourceCode}', '{resourceId}'].some((field) => template.includes(field));
  if (!hasResource || !hasTimeBoundary) {
    problems.push({
      key: 'enforcement-template-fields',
      message: '违规消息模板至少需要 {resourceCode}/{resourceId} 之一，以及 {dueAt}/{graceEnd}/{timeWindow} 之一。',
    });
  }
  if (policy.startRule.kind === 'EVENT') {
    problems.push(...validateCycleEvent(policy.startRule.event, '计时启动事件'));
  } else if (!RFC3339_WITH_OFFSET.test(policy.startRule.fixedDateTime)) {
    problems.push({ key: 'start-rfc3339', message: '固定启动时刻必须是带时区偏移的完整时间。' });
  }
  if (policy.refreshRule.kind === 'RESET_ON_EVENT') {
    problems.push(...validateCycleEvent(policy.refreshRule.event, '周期推进事件'));
  }
  if (policy.refreshRule.kind === 'ROLL_FORWARD_FROM_DUE' && !isRollableCycleRule(policy.expiryRule)) {
    problems.push({ key: 'refresh-rollability', message: '当前到期判定不能从上次到期点继续推进，请改为事件后重新起算或仅执行一次。' });
  }
  if (policy.expiryRule.kind === 'ABSOLUTE_DATE_TIME' && policy.refreshRule.kind !== 'ONE_SHOT') {
    problems.push({ key: 'absolute-one-shot', message: '固定截止时刻只能配置为仅执行一次。' });
  }
  return problems;
};
